/*
 * Settings.jsx
 * ------------
 * User settings page with four sections:
 * Appearance (theme, accent, font size),
 * Notifications (email digest, reminders),
 * Smart Mirror (token generation, copy, revoke),
 * and Account (name, email, sign out).
 * Also includes a Budget app promo at the bottom.
 * Authenticated users only -- route guarded in App.jsx.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import { useTheme } from "../App";

// ============================================
// SECTION WRAPPER
// Consistent card layout for each settings group
// ============================================
function SettingsSection({ title, description, children }) {
  return (
    <div style={{
      background: "var(--bg-surface)",
      border: "1px solid var(--border)",
      borderRadius: "var(--radius-lg)",
      overflow: "hidden"
    }}>
      <div style={{
        padding: "var(--space-5) var(--space-6)",
        borderBottom: "1px solid var(--border)",
        background: "var(--bg-surface-2)"
      }}>
        <h2 style={{
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: "18px",
          color: "var(--text-primary)",
          margin: "0 0 4px"
        }}>
          {title}
        </h2>
        {description && (
          <p style={{
            fontFamily: "var(--font-body)",
            fontSize: "13px",
            color: "var(--text-muted)",
            margin: 0
          }}>
            {description}
          </p>
        )}
      </div>
      <div style={{ padding: "var(--space-5) var(--space-6)" }}>
        {children}
      </div>
    </div>
  );
}

// ============================================
// SETTINGS ROW
// Label + control on one line
// ============================================
function SettingsRow({ label, description, children }) {
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "var(--space-4)",
      padding: "var(--space-3) 0",
      borderBottom: "1px solid var(--border)"
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          fontFamily: "var(--font-body)",
          fontWeight: 500,
          fontSize: "14px",
          color: "var(--text-primary)",
          margin: "0 0 2px"
        }}>
          {label}
        </p>
        {description && (
          <p style={{
            fontFamily: "var(--font-body)",
            fontSize: "12px",
            color: "var(--text-muted)",
            margin: 0
          }}>
            {description}
          </p>
        )}
      </div>
      <div style={{ flexShrink: 0 }}>
        {children}
      </div>
    </div>
  );
}

// ============================================
// TOGGLE SWITCH
// ============================================
function Toggle({ value, onChange }) {
  return (
    <button
      onClick={() => onChange(!value)}
      style={{
        width: "44px",
        height: "24px",
        borderRadius: "100px",
        background: value ? "var(--accent-green)" : "var(--border-strong)",
        border: "none",
        cursor: "pointer",
        position: "relative",
        transition: "background var(--transition-fast)"
      }}
    >
      <div style={{
        width: "18px",
        height: "18px",
        borderRadius: "50%",
        background: "white",
        position: "absolute",
        top: "3px",
        left: value ? "23px" : "3px",
        transition: "left var(--transition-fast)",
        boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
      }} />
    </button>
  );
}

// ============================================
// ACCENT COLOR PICKER
// ============================================
const ACCENT_COLORS = [
  { name: "Forest",  green: "#2D6A4F", gold: "#C9901A" },
  { name: "Ocean",   green: "#0077B6", gold: "#F4A261" },
  { name: "Berry",   green: "#6A2D70", gold: "#F4C842" },
  { name: "Slate",   green: "#374151", gold: "#D97706" },
  { name: "Rose",    green: "#9F1239", gold: "#F59E0B" },
  { name: "Teal",    green: "#0D9488", gold: "#EAB308" },
];

function AccentColorPicker({ selected, onChange }) {
  return (
    <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
      {ACCENT_COLORS.map(color => (
        <button
          key={color.name}
          onClick={() => onChange(color)}
          title={color.name}
          style={{
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            background: "linear-gradient(135deg, " + color.green + " 50%, " + color.gold + " 50%)",
            border: selected?.name === color.name
              ? "3px solid var(--text-primary)"
              : "3px solid transparent",
            cursor: "pointer",
            transition: "border-color var(--transition-fast), transform var(--transition-fast)",
            outline: "none"
          }}
          onMouseEnter={e => e.currentTarget.style.transform = "scale(1.15)"}
          onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
        />
      ))}
    </div>
  );
}

// ============================================
// FONT SIZE SELECTOR
// ============================================
function FontSizeSelector({ value, onChange }) {
  const options = [
    { value: "normal", label: "Normal" },
    { value: "large",  label: "Large" },
    { value: "xl",     label: "Extra Large" }
  ];

  return (
    <div style={{ display: "flex", gap: "var(--space-2)" }}>
      {options.map(opt => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          style={{
            background: value === opt.value ? "var(--accent-green)" : "none",
            border: "1px solid " + (value === opt.value ? "var(--accent-green)" : "var(--border)"),
            borderRadius: "var(--radius-sm)",
            padding: "var(--space-1) var(--space-3)",
            cursor: "pointer",
            fontFamily: "var(--font-body)",
            fontSize: "13px",
            fontWeight: value === opt.value ? 600 : 400,
            color: value === opt.value ? "white" : "var(--text-secondary)",
            transition: "all var(--transition-fast)"
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ============================================
// SMART MIRROR TOKEN SECTION
// ============================================
function SmartMirrorSection({ userId }) {
  const [token, setToken] = useState(null);
  const [copied, setCopied] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [mirrorLayout, setMirrorLayout] = useState(
    localStorage.getItem("mirrorLayout") || "comfortable"
  );

  useEffect(() => {
    const stored = localStorage.getItem("mirrorToken");
    if (stored) setToken(stored);
  }, []);

  // Generate token -- hashed version stored in Cosmos
  // Raw token shown once to user and stored locally
  async function handleGenerate() {
    setGenerating(true);
    try {
      const rawToken = crypto.randomUUID() + "-" + crypto.randomUUID();

      // Hash the token
      const encoder = new TextEncoder();
      const data = encoder.encode(rawToken);
      const hashBuffer = await crypto.subtle.digest("SHA-256", data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashedToken = hashArray.map(b => b.toString(16).padStart(2, "0")).join("");

      // Save hash to Cosmos via notifications container (user_settings doc)
      await fetch("/api/notifications", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          updateSettings: true,
          mirrorTokenHash: hashedToken
        })
      });

      // Store raw token locally
      localStorage.setItem("mirrorToken", rawToken);
      setToken(rawToken);
    } catch (err) {
      console.error("Failed to generate token", err);
    } finally {
      setGenerating(false);
    }
  }

  async function handleRevoke() {
    setRevoking(true);
    try {
      await fetch("/api/notifications", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          updateSettings: true,
          mirrorTokenHash: null
        })
      });
      localStorage.removeItem("mirrorToken");
      setToken(null);
    } catch (err) {
      console.error("Failed to revoke token", err);
    } finally {
      setRevoking(false);
    }
  }

  function handleCopy() {
    if (!token) return;
    const url = window.location.origin + "/display?token=" + token;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleLayoutChange(layout) {
    setMirrorLayout(layout);
    localStorage.setItem("mirrorLayout", layout);
  }

  const displayUrl = token
    ? window.location.origin + "/display?token=" + token
    : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>

      {/* Layout selector */}
      <SettingsRow
        label="Display Layout"
        description="How items appear on the smart mirror screen"
      >
        <div style={{ display: "flex", gap: "var(--space-2)" }}>
          {["compact", "comfortable", "large"].map(layout => (
            <button
              key={layout}
              onClick={() => handleLayoutChange(layout)}
              style={{
                background: mirrorLayout === layout ? "var(--accent-green)" : "none",
                border: "1px solid " + (mirrorLayout === layout ? "var(--accent-green)" : "var(--border)"),
                borderRadius: "var(--radius-sm)",
                padding: "var(--space-1) var(--space-3)",
                cursor: "pointer",
                fontFamily: "var(--font-body)",
                fontSize: "12px",
                fontWeight: mirrorLayout === layout ? 600 : 400,
                color: mirrorLayout === layout ? "white" : "var(--text-secondary)",
                textTransform: "capitalize",
                transition: "all var(--transition-fast)"
              }}
            >
              {layout}
            </button>
          ))}
        </div>
      </SettingsRow>

      {/* Token management */}
      <div style={{
        background: "var(--bg-surface-2)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-md)",
        padding: "var(--space-4)"
      }}>
        {token ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
            <p style={{
              fontFamily: "var(--font-body)",
              fontSize: "13px",
              fontWeight: 600,
              color: "var(--accent-green)",
              margin: 0,
              display: "flex",
              alignItems: "center",
              gap: "var(--space-2)"
            }}>
              ✅ Smart mirror link is active
            </p>

            {/* URL display */}
            <div style={{
              background: "var(--bg-surface)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              padding: "var(--space-2) var(--space-3)",
              fontFamily: "monospace",
              fontSize: "12px",
              color: "var(--text-muted)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}>
              {displayUrl}
            </div>

            <div style={{ display: "flex", gap: "var(--space-2)" }}>
              <button
                onClick={handleCopy}
                style={{
                  flex: 1,
                  background: copied ? "var(--accent-green-muted)" : "none",
                  border: "1px solid " + (copied ? "var(--accent-green)" : "var(--border)"),
                  borderRadius: "var(--radius-sm)",
                  padding: "var(--space-2) var(--space-3)",
                  cursor: "pointer",
                  fontFamily: "var(--font-body)",
                  fontSize: "13px",
                  fontWeight: 500,
                  color: copied ? "var(--accent-green)" : "var(--text-secondary)",
                  transition: "all var(--transition-fast)"
                }}
              >
                {copied ? "✓ Copied!" : "Copy Link"}
              </button>
              <button
                onClick={handleGenerate}
                disabled={generating}
                style={{
                  background: "none",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-sm)",
                  padding: "var(--space-2) var(--space-3)",
                  cursor: "pointer",
                  fontFamily: "var(--font-body)",
                  fontSize: "13px",
                  color: "var(--text-secondary)"
                }}
              >
                Regenerate
              </button>
              <button
                onClick={handleRevoke}
                disabled={revoking}
                style={{
                  background: "none",
                  border: "1px solid var(--status-overdue)",
                  borderRadius: "var(--radius-sm)",
                  padding: "var(--space-2) var(--space-3)",
                  cursor: "pointer",
                  fontFamily: "var(--font-body)",
                  fontSize: "13px",
                  color: "var(--status-overdue)"
                }}
              >
                {revoking ? "Revoking..." : "Revoke"}
              </button>
            </div>

            <p style={{
              fontFamily: "var(--font-body)",
              fontSize: "11px",
              color: "var(--text-muted)",
              margin: 0
            }}>
              This link gives read-only access to your Today and Upcoming items. Only share it with trusted devices.
            </p>
          </div>
        ) : (
          <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "var(--space-3)",
            textAlign: "center",
            padding: "var(--space-4) 0"
          }}>
            <span style={{ fontSize: "32px" }}>🪞</span>
            <div>
              <p style={{
                fontFamily: "var(--font-body)",
                fontWeight: 600,
                fontSize: "14px",
                color: "var(--text-primary)",
                margin: "0 0 4px"
              }}>
                No smart mirror link yet
              </p>
              <p style={{
                fontFamily: "var(--font-body)",
                fontSize: "13px",
                color: "var(--text-muted)",
                margin: 0
              }}>
                Generate a secure link to display your checklist on a smart mirror or any screen.
              </p>
            </div>
            <button
              onClick={handleGenerate}
              disabled={generating}
              style={{
                background: "var(--accent-green)",
                border: "none",
                borderRadius: "var(--radius-sm)",
                padding: "var(--space-2) var(--space-5)",
                cursor: "pointer",
                fontFamily: "var(--font-body)",
                fontSize: "14px",
                fontWeight: 600,
                color: "white",
                opacity: generating ? 0.7 : 1
              }}
            >
              {generating ? "Generating..." : "Generate Link"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// BUDGET PROMO
// ============================================
function BudgetPromo() {
  const [hovered, setHovered] = useState(false);

  return (<a
    
      href="https://budget.rioscloud.dev"
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--space-4)",
        background: "var(--bg-surface)",
        border: hovered ? "1px solid var(--accent-gold)" : "1px solid var(--border)",
        borderRadius: "var(--radius-lg)",
        padding: "var(--space-5)",
        textDecoration: "none",
        transition: "border-color var(--transition-fast), box-shadow var(--transition-fast)",
        boxShadow: hovered ? "var(--shadow-md)" : "none"
      }}
    >
      <div style={{
        width: "48px",
        height: "48px",
        borderRadius: "var(--radius-md)",
        background: "var(--accent-gold-muted)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "24px",
        flexShrink: 0
      }}>
        💰
      </div>
      <div style={{ flex: 1 }}>
        <p style={{
          fontFamily: "var(--font-body)",
          fontWeight: 600,
          fontSize: "15px",
          color: "var(--text-primary)",
          margin: "0 0 4px"
        }}>
          Also from rioscloud -- Budget
        </p>
        <p style={{
          fontFamily: "var(--font-body)",
          fontSize: "13px",
          color: "var(--text-secondary)",
          margin: 0
        }}>
          Take control of your finances. Track spending, set goals, and build better money habits.
        </p>
      </div>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ color: "var(--text-muted)", flexShrink: 0 }}>
        <path d="M3 8H13M9 4L13 8L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </a>
  );
}

// ============================================
// MAIN EXPORT
// ============================================
export default function Settings() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();

  // Appearance
  const [accentColor, setAccentColor] = useState(() => {
    const stored = localStorage.getItem("accentColor");
    return stored ? JSON.parse(stored) : ACCENT_COLORS[0];
  });
  const [fontSize, setFontSize] = useState(
    localStorage.getItem("fontSize") || "normal"
  );

  // Notifications
  const [emailDigest, setEmailDigest] = useState(false);
  const [emailAddress, setEmailAddress] = useState(user?.name || "");
  const [digestTime, setDigestTime] = useState("07:00");
  const [reminderHours, setReminderHours] = useState("2");
  const [weeklyRecap, setWeeklyRecap] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // ============================================
  // LOAD USER SETTINGS FROM COSMOS
  // ============================================
  const loadSettings = useCallback(async () => {
    try {
      const res = await fetch("/api/notifications?type=user_settings", {
        headers: { "Content-Type": "application/json" }
      });
      const data = await res.json();
      const settings = data.find(d => d.type === "user_settings");
      if (settings) {
        setEmailDigest(settings.emailDigest || false);
        setEmailAddress(settings.emailAddress || "");
        setDigestTime(settings.digestTime || "07:00");
        setReminderHours(settings.reminderHours || "2");
        setWeeklyRecap(settings.weeklyRecap || false);
      }
    } catch (err) {
      console.error("Failed to load settings", err);
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  // ============================================
  // APPLY ACCENT COLOR
  // ============================================
  useEffect(() => {
    document.documentElement.style.setProperty("--accent-green", accentColor.green);
    document.documentElement.style.setProperty("--accent-gold", accentColor.gold);
    localStorage.setItem("accentColor", JSON.stringify(accentColor));
  }, [accentColor]);

  // ============================================
  // APPLY FONT SIZE
  // ============================================
  useEffect(() => {
    const sizes = { normal: "16px", large: "18px", xl: "20px" };
    document.documentElement.style.fontSize = sizes[fontSize] || "16px";
    localStorage.setItem("fontSize", fontSize);
  }, [fontSize]);

  // ============================================
  // SAVE NOTIFICATION SETTINGS
  // ============================================
  async function handleSaveNotifications() {
    setSaving(true);
    try {
      await fetch("/api/notifications", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          updateSettings: true,
          emailDigest,
          emailAddress,
          digestTime,
          reminderHours,
          weeklyRecap
        })
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error("Failed to save settings", err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      maxWidth: "700px",
      margin: "0 auto",
      padding: "var(--space-6) var(--space-4)",
      display: "flex",
      flexDirection: "column",
      gap: "var(--space-5)"
    }}>

      {/* Header */}
      <div>
        <h1 style={{
          fontFamily: "var(--font-display)",
          fontWeight: 900,
          fontSize: "clamp(28px, 5vw, 40px)",
          color: "var(--text-primary)",
          margin: "0 0 var(--space-2)",
          letterSpacing: "-1px"
        }}>
          Settings
        </h1>
      </div>

      {/* Appearance */}
      <SettingsSection
        title="Appearance"
        description="Customize how Checklist looks and feels"
      >
        <SettingsRow label="Theme" description="Switch between light and dark mode">
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
            <span style={{ fontFamily: "var(--font-body)", fontSize: "13px", color: "var(--text-muted)" }}>
              {theme === "light" ? "☀️ Light" : "🌙 Dark"}
            </span>
            <Toggle value={theme === "dark"} onChange={() => toggleTheme()} />
          </div>
        </SettingsRow>

        <SettingsRow label="Accent Color" description="Choose your color palette">
          <AccentColorPicker selected={accentColor} onChange={setAccentColor} />
        </SettingsRow>

        <SettingsRow label="Font Size" description="Adjust text size across the app">
          <FontSizeSelector value={fontSize} onChange={setFontSize} />
        </SettingsRow>
      </SettingsSection>

      {/* Notifications */}
      <SettingsSection
        title="Notifications"
        description="Manage your email preferences"
      >
        {settingsLoading ? (
          <p style={{ fontFamily: "var(--font-body)", color: "var(--text-muted)", fontSize: "14px" }}>
            Loading...
          </p>
        ) : (
          <>
            <SettingsRow label="Email Address" description="Where we send your notifications">
              <input
                type="email"
                value={emailAddress}
                onChange={e => setEmailAddress(e.target.value)}
                placeholder="you@example.com"
                style={{
                  fontFamily: "var(--font-body)",
                  fontSize: "14px",
                  color: "var(--text-primary)",
                  background: "var(--bg-surface-2)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-sm)",
                  padding: "var(--space-2) var(--space-3)",
                  outline: "none",
                  width: "200px",
                  transition: "border-color var(--transition-fast)"
                }}
                onFocus={e => e.target.style.borderColor = "var(--accent-green)"}
                onBlur={e => e.target.style.borderColor = "var(--border)"}
              />
            </SettingsRow>

            <SettingsRow
              label="Daily Digest"
              description="Morning email with your overdue, today, and upcoming items"
            >
              <Toggle value={emailDigest} onChange={setEmailDigest} />
            </SettingsRow>

            {emailDigest && (
              <SettingsRow label="Digest Time" description="What time to send the daily email">
                <input
                  type="time"
                  value={digestTime}
                  onChange={e => setDigestTime(e.target.value)}
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: "14px",
                    color: "var(--text-primary)",
                    background: "var(--bg-surface-2)",
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius-sm)",
                    padding: "var(--space-2) var(--space-3)",
                    outline: "none"
                  }}
                />
              </SettingsRow>
            )}

            <SettingsRow
              label="Due Date Reminders"
              description="Email reminder before an item is due"
            >
              <select
                value={reminderHours}
                onChange={e => setReminderHours(e.target.value)}
                style={{
                  fontFamily: "var(--font-body)",
                  fontSize: "13px",
                  color: "var(--text-secondary)",
                  background: "var(--bg-surface)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-sm)",
                  padding: "var(--space-2) var(--space-3)",
                  cursor: "pointer",
                  outline: "none"
                }}
              >
                <option value="1">1 hour before</option>
                <option value="2">2 hours before</option>
                <option value="24">24 hours before</option>
                <option value="0">Off</option>
              </select>
            </SettingsRow>

            <SettingsRow
              label="Weekly Recap"
              description="Sunday evening summary of what you completed this week"
            >
              <Toggle value={weeklyRecap} onChange={setWeeklyRecap} />
            </SettingsRow>

            {/* Save button */}
            <div style={{ paddingTop: "var(--space-4)", display: "flex", justifyContent: "flex-end" }}>
              <button
                onClick={handleSaveNotifications}
                disabled={saving}
                style={{
                  background: saved ? "var(--accent-green-muted)" : "var(--accent-green)",
                  border: saved ? "1px solid var(--accent-green)" : "none",
                  borderRadius: "var(--radius-sm)",
                  padding: "var(--space-2) var(--space-5)",
                  cursor: saving ? "not-allowed" : "pointer",
                  fontFamily: "var(--font-body)",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: saved ? "var(--accent-green)" : "white",
                  opacity: saving ? 0.7 : 1,
                  transition: "all var(--transition-fast)"
                }}
              >
                {saving ? "Saving..." : saved ? "✓ Saved" : "Save Preferences"}
              </button>
            </div>
          </>
        )}
      </SettingsSection>

      {/* Smart Mirror */}
      <SettingsSection
        title="Smart Mirror"
        description="Generate a secure read-only link for your smart mirror or display screen"
      >
        <SmartMirrorSection userId={user?.id} />
      </SettingsSection>

      {/* Account */}
      <SettingsSection
        title="Account"
        description="Your account information"
      >
        <SettingsRow label="Name" description="Your Microsoft account name">
          <span style={{
            fontFamily: "var(--font-body)",
            fontSize: "14px",
            color: "var(--text-secondary)"
          }}>
            {user?.name || "Unknown"}
          </span>
        </SettingsRow>

        <div style={{ paddingTop: "var(--space-4)" }}>
          <button
            onClick={logout}
            style={{
              background: "none",
              border: "1px solid var(--status-overdue)",
              borderRadius: "var(--radius-sm)",
              padding: "var(--space-2) var(--space-5)",
              cursor: "pointer",
              fontFamily: "var(--font-body)",
              fontSize: "14px",
              fontWeight: 600,
              color: "var(--status-overdue)",
              transition: "background var(--transition-fast)"
            }}
            onMouseEnter={e => e.currentTarget.style.background = "var(--status-overdue-muted)"}
            onMouseLeave={e => e.currentTarget.style.background = "none"}
          >
            Sign Out
          </button>
        </div>
      </SettingsSection>

      {/* Budget promo */}
      <BudgetPromo />

    </div>
  );
}